import './UsersListRow.css';

import { FunctionComponent, useState } from 'react';
import { InvoicingAccountOptionResponseContents } from '@cdaas/api-clients/dist/asset-user-openapi';
import { Dropdown, Flex, Icon, Input, Table, BodyText, PhoneNumberInput, CountyCodeShape, Div } from '@gaiads/telia-react-component-library';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { saveUser, showGrowlNotification, closeGrowlNotification } from '../../../store/actions';
import { UserGroupNameDropdownOption } from './UsersList';
import { isValidEmail, isValidPhoneNumber } from '../../../utils';
import { COUNTRY_CALLING_CODES } from '../../../utils/constants';
import { User } from '../../../types';
import { RootState } from '../../../store/reducers';

type UserGroupsListRowProps = {
  user: User;
  userIndex: number;
  userGroupNameDropdownOptions: UserGroupNameDropdownOption[];
  organizationInvoicingAccounts: InvoicingAccountOptionResponseContents[];
  handleInputChange: (
    value: string | null,
    userIndex: number,
    key: 'email' | 'userGroupName' | 'phoneNumber' | 'countryCode' | 'employeeId' | 'invoicingAccount',
  ) => void;
  handleDeleteUser: (user: User) => void;
};

const UsersListRow: FunctionComponent<UserGroupsListRowProps> = ({
  user,
  userIndex,
  userGroupNameDropdownOptions,
  organizationInvoicingAccounts,
  handleInputChange,
  handleDeleteUser,
}) => {
  const organization = useSelector((state: RootState) => state.authentication.user?.organization);
  const [emailError, setEmailError] = useState(false);
  const [groupError, setGroupError] = useState(false);
  const [phoneNumberError, setPhoneNumberError] = useState(false);
  const [countryCode, setCountryCode] = useState<CountyCodeShape>();
  const [isSaving, setIsSaving] = useState(false);
  const dispatch = useDispatch();
  const tOnboarding = useTranslation('onboarding').t;
  const tGlobalComponents = useTranslation('globalComponents').t;

  const saveUserRow = (user: User) => {
    setIsSaving(true);
    return dispatch(saveUser(user)).finally(() => setIsSaving(false));
  };

  const invoicingAccountOptions = organizationInvoicingAccounts.map(invoicingAccount => ({
    label: invoicingAccount.name,
    value: invoicingAccount.id,
  }));
  let selectedInvoicingAccountOption = invoicingAccountOptions.find(
    invoicingAccountOption => invoicingAccountOption.value === user.invoicing_account?.id,
  );
  // Show account info and error message if invoicing account used by this user has been removed from Telia Finance
  let invoiceAccountNoLongerExists = false;
  if (user.invoicing_account && !user.invoicing_account.active) {
    selectedInvoicingAccountOption = { label: user.invoicing_account.name, value: user.invoicing_account.id };
    invoiceAccountNoLongerExists = true;
  }
  // In the dropdown options list also show an option for not having a dedicated invoicing account for this user
  const noInvoicingAccountValue = '-1';
  invoicingAccountOptions.unshift({
    label: tOnboarding('userPage.selectNoInvoicingAccount', {
      inheritedInvoicingAccount: user.inherited_invoicing_account?.name || organization?.call_off_number,
      interpolation: { escapeValue: false },
    }),
    value: noInvoicingAccountValue,
  });

  const handleSaveButton = () => {
    if (!user.modified) return;
    if (
      !user.email ||
      !user.user_group ||
      !user.phone_number ||
      emailError ||
      groupError ||
      phoneNumberError ||
      (user.newItem && !user.country_calling_code)
    ) {
      dispatch(showGrowlNotification(tOnboarding('userFillInformationNotification'), 'warning'));
      return;
    }
    saveUserRow(user);
    dispatch(closeGrowlNotification());
  };

  const emailInput = (
    <Input
      placeholder={tGlobalComponents('InputPlaceholder.email')}
      showCustomLabel={true}
      label={''}
      value={user.email}
      onChange={e => {
        handleInputChange(e.target.value, userIndex, 'email');
        if (isValidEmail(e.target.value)) setEmailError(false);
        else setEmailError(true);
      }}
      errorText={emailError ? ' ' : ''}
    />
  );

  const phoneNumberInput = (
    <Div className="remove-extra-gutter">
      <PhoneNumberInput
        label=" "
        countryCodes={COUNTRY_CALLING_CODES}
        numberValue={user.phone_number ?? undefined}
        onNumberChange={(_e, value) => {
          handleInputChange(value, userIndex, 'phoneNumber');
          if (isValidPhoneNumber(value)) setPhoneNumberError(false);
          else setPhoneNumberError(true);
        }}
        onCountryCodeChange={(_e, value) => {
          handleInputChange(value?.code || '', userIndex, 'countryCode');
          setCountryCode(value);
        }}
        selectedCountryCode={countryCode}
        errorText={phoneNumberError ? ' ' : ''}
      />
    </Div>
  );

  const employeeIdInput = (
    <Input
      showCustomLabel={true}
      label={''}
      value={user.employee_id ?? ''}
      onChange={e => {
        handleInputChange(e.target.value, userIndex, 'employeeId');
      }}
    />
  );

  return (
    <Table.Row key={`table.row-${userIndex}`}>
      {/* User email */}
      <Table.Cell width={'22%'}>
        <Flex>
          {user.newItem ? null : (
            <Icon
              name="check-circle-filled"
              size="md"
              margin={{ right: 'xs' }}
              color={user.name && user.name !== 'default_name' ? 'green500' : 'gray400'}
            />
          )}
          {user.name && user.name !== 'default_name' ? (
            <BodyText>{user.name}</BodyText>
          ) : user.newItem ? (
            emailInput
          ) : (
            <BodyText>{user.email}</BodyText>
          )}
        </Flex>
      </Table.Cell>

      {/* Phone number */}
      <Table.Cell>{user.newItem ? phoneNumberInput : <BodyText>{user.phone_number}</BodyText>}</Table.Cell>

      {/* Group dropdown */}
      <Table.Cell>
        <Dropdown
          showCustomLabel={true}
          onChange={(_event, option) => {
            handleInputChange(option.value, userIndex, 'userGroupName');
            if (option.value) setGroupError(false);
            else setGroupError(true);
          }}
          selectedOption={user.user_group?.name ? { label: user.user_group.name, value: user.user_group.name } : undefined}
          options={userGroupNameDropdownOptions}
          errorText={groupError ? ' ' : ''}
        />
      </Table.Cell>

      {/* Invoicing account dropdown */}
      <Table.Cell>
        <Dropdown
          showCustomLabel={true}
          onChange={(_event, option) => {
            const invoicingAccountId = option.value === noInvoicingAccountValue ? null : option.value;
            handleInputChange(invoicingAccountId, userIndex, 'invoicingAccount');
          }}
          selectedOption={selectedInvoicingAccountOption}
          options={invoicingAccountOptions}
          errorText={invoiceAccountNoLongerExists ? tOnboarding('userPage.userHasNoLongerExistingInvoicingAccountError') : ''}
        />
      </Table.Cell>

      {/* Employee ID */}
      <Table.Cell>
        <Flex centered>
          {employeeIdInput}
          <Icon
            clickableAppearance={user.modified && !isSaving}
            onClick={!isSaving ? handleSaveButton : undefined}
            color={user.modified && !isSaving ? 'green700' : 'gray500'}
            margin={{ left: 'md' }}
            name={user.modified ? 'save' : 'checkmark'}
            size={'md'}
          />
          <Icon clickableAppearance onClick={() => handleDeleteUser(user)} color={'gray700'} margin={{ left: 'md' }} name={'delete'} size={'md'} />
        </Flex>
      </Table.Cell>
    </Table.Row>
  );
};

export default UsersListRow;
